// Client-side "Last ned PDF" for reports that are built in the browser, such as
// the personal finance report. The report element is rendered with html2canvas
// and laid out on A4 pages with jsPDF. Both libraries are loaded on first use
// only, pinned and with SRI, never in <head>. html2canvas is the same pinned
// build as src/services/supportScreenshot.js.
(function (global) {
  const HTML2CANVAS_SCRIPT = 'https://cdn.jsdelivr.net/npm/html2canvas@1.4.1/dist/html2canvas.min.js';
  const HTML2CANVAS_INTEGRITY = 'sha512-BNaRQnYJYiPSqHHDb58B0yaPfCu+Wgds8Gp/gU33kqBtgNS4tSPHuGibyoeqMV/TJlSKda6FXzoEyYGjTe+vXA==';
  const JSPDF_SCRIPT = 'https://cdn.jsdelivr.net/npm/jspdf@4.2.1/dist/jspdf.umd.min.js';
  const JSPDF_INTEGRITY = 'sha512-plOdviVmws4Y3JAvbnpfKb2hVxKM1lCwsi3vmElYRj+tiDLffZ4FVUj5a8vyKJ9pIgl8JCAHEJ4D1iUKBecswg==';
  const PAGE_MARGIN_MM = 10;
  const EXCLUDE_ATTRIBUTE = 'data-pdf-exclude';

  const loading = {};
  const loadScript = (key, src, integrity, ready) => {
    const existing = ready();
    if (existing) return Promise.resolve(existing);
    if (loading[key]) return loading[key];
    loading[key] = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = src;
      script.async = true;
      script.integrity = integrity;
      script.crossOrigin = 'anonymous';
      script.onload = () => (ready() ? resolve(ready()) : reject(new Error('report-pdf-renderer-unavailable')));
      script.onerror = () => {
        loading[key] = null;
        reject(new Error('report-pdf-renderer-unavailable'));
      };
      document.head.appendChild(script);
    });
    return loading[key];
  };

  const loadHtml2canvas = () => loadScript('html2canvas', HTML2CANVAS_SCRIPT, HTML2CANVAS_INTEGRITY, () => global.html2canvas);
  const loadJsPdf = () => loadScript('jspdf', JSPDF_SCRIPT, JSPDF_INTEGRITY, () => global.jspdf?.jsPDF);

  const safeFileName = (name) => {
    const base = String(name || 'plumme-rapport')
      .trim()
      .replace(/\.pdf$/i, '')
      .replace(/[^\p{L}\p{N}._-]+/gu, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '')
      .slice(0, 120);
    return `${base || 'plumme-rapport'}.pdf`;
  };

  const isTransparent = (color) => !color || color === 'transparent' || /rgba\([^)]*,\s*0\)$/.test(color);
  const backgroundOf = (element) => {
    const own = global.getComputedStyle?.(element)?.backgroundColor;
    if (!isTransparent(own)) return own;
    const body = global.getComputedStyle?.(document.body)?.backgroundColor;
    return isTransparent(body) ? '#ffffff' : body;
  };

  // Splits one tall canvas into A4 page slices. Returns the slices in canvas
  // pixels so the layout can be tested without a real canvas.
  const pageSlices = (canvasWidth, canvasHeight, pageWidthMm, pageHeightMm, marginMm = PAGE_MARGIN_MM) => {
    const contentWidthMm = pageWidthMm - marginMm * 2;
    const contentHeightMm = pageHeightMm - marginMm * 2;
    const pxPerMm = canvasWidth / contentWidthMm;
    const slicePx = Math.max(1, Math.floor(contentHeightMm * pxPerMm));
    const slices = [];
    for (let top = 0; top < canvasHeight; top += slicePx) {
      const height = Math.min(slicePx, canvasHeight - top);
      slices.push({ top, height, widthMm: contentWidthMm, heightMm: height / pxPerMm });
    }
    return slices;
  };

  const sliceToDataUrl = (canvas, slice) => {
    if (slice.top === 0 && slice.height === canvas.height) return canvas.toDataURL('image/jpeg', 0.92);
    const page = document.createElement('canvas');
    page.width = canvas.width;
    page.height = slice.height;
    const context = page.getContext('2d');
    context.drawImage(canvas, 0, slice.top, canvas.width, slice.height, 0, 0, canvas.width, slice.height);
    return page.toDataURL('image/jpeg', 0.92);
  };

  const elementToPdfBlob = async (element, { renderer, JsPdf } = {}) => {
    if (!element) throw new Error('report-pdf-missing-element');
    const [render, PdfDocument] = await Promise.all([
      renderer || loadHtml2canvas(),
      JsPdf || loadJsPdf(),
    ]);
    await document.fonts?.ready?.catch?.(() => {});
    const canvas = await render(element, {
      allowTaint: false,
      useCORS: true,
      backgroundColor: backgroundOf(element),
      logging: false,
      scale: Math.min(2, Math.max(1, Number(global.devicePixelRatio) || 1)),
      ignoreElements: (node) => Boolean(node?.hasAttribute?.(EXCLUDE_ATTRIBUTE)),
    });
    const pdf = new PdfDocument({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    pageSlices(canvas.width, canvas.height, pageWidth, pageHeight).forEach((slice, index) => {
      if (index > 0) pdf.addPage();
      pdf.addImage(sliceToDataUrl(canvas, slice), 'JPEG', PAGE_MARGIN_MM, PAGE_MARGIN_MM, slice.widthMm, slice.heightMm);
    });
    return pdf.output('blob');
  };

  const saveBlob = (blob, fileName) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const downloadElementAsPdf = async (element, { fileName, renderer, JsPdf } = {}) => {
    const blob = await elementToPdfBlob(element, { renderer, JsPdf });
    const name = safeFileName(fileName);
    saveBlob(blob, name);
    return { fileName: name, size: blob.size };
  };

  global.PlummeReportPdf = Object.freeze({
    EXCLUDE_ATTRIBUTE,
    JSPDF_SCRIPT,
    HTML2CANVAS_SCRIPT,
    loadHtml2canvas,
    loadJsPdf,
    safeFileName,
    pageSlices,
    elementToPdfBlob,
    downloadElementAsPdf,
  });
})(window);
